import { useCallback } from 'react';
import type { AssetsSearchOrderByField } from '@terra/shared-contracts-assets';
import { createTabScopedStore } from './createTabScopedStore';

export const ASSETS_TABLE_GROUP_BY = {
  NONE: 'none',
  DOMAIN: 'domain',
  METHOD: 'method',
} as const;

export type AssetsTableGroupBy =
  (typeof ASSETS_TABLE_GROUP_BY)[keyof typeof ASSETS_TABLE_GROUP_BY];

export type AssetsTableSort = {
  field: AssetsSearchOrderByField;
  order: 'asc' | 'desc';
};

type AssetsTableViewState = {
  assetsTableGroupBy: AssetsTableGroupBy;
  assetsTableSort: AssetsTableSort | null;
  assetsTableCollapsedGroupKeys: string[];
};

const useAssetsTableViewStoreBase = createTabScopedStore<AssetsTableViewState>({
  assetsTableGroupBy: ASSETS_TABLE_GROUP_BY.DOMAIN,
  assetsTableSort: null,
  assetsTableCollapsedGroupKeys: [],
});

export const toggleAssetsTableGroupKey = ({
  collapsedGroupKeys,
  groupKey,
}: {
  collapsedGroupKeys: string[];
  groupKey: string;
}): string[] =>
  collapsedGroupKeys.includes(groupKey)
    ? collapsedGroupKeys.filter((key) => key !== groupKey)
    : [...collapsedGroupKeys, groupKey];

export function useTabScopedAssetsTableView() {
  const { filters, update } = useAssetsTableViewStoreBase();

  const setGroupBy = useCallback(
    (groupBy: AssetsTableGroupBy) =>
      update({
        assetsTableGroupBy: groupBy,
        assetsTableCollapsedGroupKeys: [],
      }),
    [update],
  );
  const setSort = useCallback(
    (sort: AssetsTableSort | null) => update({ assetsTableSort: sort }),
    [update],
  );
  const setCollapsedGroupKeys = useCallback(
    (collapsedGroupKeys: string[]) =>
      update({ assetsTableCollapsedGroupKeys: collapsedGroupKeys }),
    [update],
  );
  const toggleGroupCollapsed = useCallback(
    (groupKey: string) =>
      update({
        assetsTableCollapsedGroupKeys: toggleAssetsTableGroupKey({
          collapsedGroupKeys: filters.assetsTableCollapsedGroupKeys,
          groupKey,
        }),
      }),
    [filters.assetsTableCollapsedGroupKeys, update],
  );

  return {
    groupBy: filters.assetsTableGroupBy,
    sort: filters.assetsTableSort,
    collapsedGroupKeys: filters.assetsTableCollapsedGroupKeys,
    setGroupBy,
    setSort,
    setCollapsedGroupKeys,
    toggleGroupCollapsed,
  };
}
